import {
    DashboardFilterParameter,
    DashboardViewFilterSectionFilters,
    WidgetForcedFilterRequirement,
} from "../types";

export type ForcedFilterFieldKey = keyof DashboardViewFilterSectionFilters;

export type ForcedFilterErrorKey = 'required' | 'min' | 'max' | 'minMax';

export interface ForcedFilterValidationItem {
    filterName: DashboardFilterParameter;
    fieldKey?: ForcedFilterFieldKey;
    met: boolean;
    count: number;
    isRequired?: boolean;
    requiredMinCount?: number;
    requiredMaxCount?: number;
    errorKey?: ForcedFilterErrorKey;
}

export interface ForcedFilterValidationResult {
    isValid: boolean;
    results: ForcedFilterValidationItem[];
}

/**
 * Maps API filter parameters to the widget/dashboard filter form fields.
 * Inverse of the field → parameter map in `use-widget-data`.
 */
export const FILTER_PARAMETER_TO_FIELD: Partial<Record<DashboardFilterParameter, ForcedFilterFieldKey>> = {
    [DashboardFilterParameter.Industry]: 'industry',
    [DashboardFilterParameter.CurrencyCode]: 'currency',
    [DashboardFilterParameter.StartDate]: 'dateRangeValue',
    [DashboardFilterParameter.EndDate]: 'dateRangeValue',
    [DashboardFilterParameter.ExcludePartNumbers]: 'excludePartNumbers',
    [DashboardFilterParameter.ExcludeCustomerIds]: 'excludeCustomers',
    [DashboardFilterParameter.PartNumbers]: 'partNumbers',
    [DashboardFilterParameter.PartNumberPrefix]: 'partNumberPrefix',
    [DashboardFilterParameter.CustomerIds]: 'customers',
    [DashboardFilterParameter.PartCategories]: 'categories',
    [DashboardFilterParameter.TerritoryCodes]: 'territoryCodes',
    [DashboardFilterParameter.PSR_ReasonIds]: 'partSupportReasons',
    [DashboardFilterParameter.PSR_StatusIds]: 'partSupportStatuses',
    [DashboardFilterParameter.PSR_Types]: 'partSupportTypes',
    [DashboardFilterParameter.PSR_VendorStatus]: 'partSupportVendorStatuses',
    [DashboardFilterParameter.WarehouseCodes]: 'warehouses',
    [DashboardFilterParameter.SalesWarehouseCodes]: 'salesWarehouses',
    [DashboardFilterParameter.OperatorIds]: 'operators',
    [DashboardFilterParameter.PSR_ExcludeReasonIds]: 'partSupportExcludeReasons',
    [DashboardFilterParameter.HasCredited]: 'hasCredited',
    [DashboardFilterParameter.ProvinceCodes]: 'provinces',
    [DashboardFilterParameter.Cities]: 'cities',
    [DashboardFilterParameter.AccountManagersCodes]: 'accountManagers',
    [DashboardFilterParameter.ArTransactionTypes]: 'arTransactionTypes',
    [DashboardFilterParameter.VendorNumbers]: 'vendorNumbers',
};

/**
 * Returns the filter-form field for a dashboard filter parameter.
 */
export const getFilterFieldKey = (filterName: DashboardFilterParameter): ForcedFilterFieldKey | undefined => {
    return FILTER_PARAMETER_TO_FIELD[filterName];
};

/**
 * Returns the form field keys that have a forced-filter requirement.
 */
export const getForcedFilterFieldKeys = (requirements?: WidgetForcedFilterRequirement[]): ForcedFilterFieldKey[] => {
    const keys = new Set<ForcedFilterFieldKey>();
    for (const requirement of requirements ?? []) {
        const fieldKey = getFilterFieldKey(requirement.filterName);
        if (fieldKey) {
            keys.add(fieldKey);
        }
    }
    return Array.from(keys);
};

/**
 * Whether any requirement is marked required (Clear would wipe a required filter).
 */
export const hasRequiredForcedFilters = (requirements?: WidgetForcedFilterRequirement[]): boolean => {
    return (requirements ?? []).some((requirement) => requirement.isRequired);
};

/**
 * Counts selected values for a filter on either serialized filters or live form values.
 */
export const getFilterValueCount = (
    filters: Partial<Record<string, unknown>> | DashboardViewFilterSectionFilters | undefined,
    filterName: DashboardFilterParameter,
): number => {
    const fieldKey = getFilterFieldKey(filterName);
    if (!fieldKey || !filters) {
        return 0;
    }
    const value = (filters as Record<string, unknown>)[fieldKey];
    if (value == null || value === '' || value === 'null') {
        return 0;
    }
    if (Array.isArray(value)) {
        return value.length;
    }
    return 1;
};

/**
 * Resolves the effective min count: required filters must have at least one value.
 */
const getEffectiveMinCount = (requirement: WidgetForcedFilterRequirement): number | undefined => {
    if (requirement.isRequired) {
        return Math.max(requirement.requiredMinCount ?? 1, 1);
    }
    return requirement.requiredMinCount;
};

/**
 * Validates widget filters against forced-filter requirements.
 *
 * - `isRequired`: count must be greater than 0 (and at least `requiredMinCount` when set).
 * - min/max are skipped when the filter is optional and currently empty.
 */
export const validateForcedFilters = (
    filters: Partial<Record<string, unknown>> | DashboardViewFilterSectionFilters | undefined,
    requirements?: WidgetForcedFilterRequirement[],
): ForcedFilterValidationResult => {
    const results: ForcedFilterValidationItem[] = (requirements ?? []).map((requirement) => {
        const count = getFilterValueCount(filters, requirement.filterName);
        const fieldKey = getFilterFieldKey(requirement.filterName);
        const effectiveMin = getEffectiveMinCount(requirement);
        const max = requirement.requiredMaxCount;
        const skipMinMax = !requirement.isRequired && count === 0;

        let errorKey: ForcedFilterErrorKey | undefined;
        if (requirement.isRequired && count === 0) {
            errorKey = 'required';
        } else if (!skipMinMax && effectiveMin != null && max != null && (count < effectiveMin || count > max)) {
            errorKey = 'minMax';
        } else if (!skipMinMax && effectiveMin != null && count < effectiveMin) {
            errorKey = 'min';
        } else if (!skipMinMax && max != null && count > max) {
            errorKey = 'max';
        }

        return {
            filterName: requirement.filterName,
            fieldKey,
            met: !errorKey,
            count,
            isRequired: requirement.isRequired,
            requiredMinCount: requirement.requiredMinCount,
            requiredMaxCount: requirement.requiredMaxCount,
            errorKey,
        };
    });

    return {
        isValid: results.every((result) => result.met),
        results,
    };
};

/**
 * Builds the filter-parameter list for the widget filter UI, ensuring required
 * parameters (and Industry when categories are involved) are always present.
 */
export const getWidgetFilterParameters = (
    parameters?: DashboardFilterParameter[],
    requirements?: WidgetForcedFilterRequirement[],
): DashboardFilterParameter[] => {
    const params = new Set<DashboardFilterParameter>(parameters ?? []);
    for (const requirement of requirements ?? []) {
        params.add(requirement.filterName);
    }
    if (params.has(DashboardFilterParameter.PartCategories)) {
        params.add(DashboardFilterParameter.Industry);
    }
    return Array.from(params);
};
